import type { League, Player, PlayerPositionEnum, RosterPositionEnum, Team, TeamPlayerInfo } from '../../sdk/model';
import { PlayerPositionEnum as PlayerPosition } from '../../sdk/model';
import { RosterPositionEnum as RosterPosition } from '../../sdk/model';
import { getMaxTeamSize, getRemainingRosterSpots, getRosterSize } from './TeamUtils';
import { toSalary } from './SalaryDisplay';

/**
 * Skill-position player counts above these values use light green (deepest depth).
 * Between league max+1 and this count uses standard green; at league max uses dark green.
 */
export const SKILL_POSITION_DEPTH_THRESHOLDS: Record<string, number> = {
  QB: 2,
  RB: 3,
  WR: 4,
  TE: 2,
};

export type PositionChipDisplay = 'empty' | 'under' | 'exact' | 'plusOne' | 'deep';

export function getPositionChipDisplay(
  label: string,
  filled: number,
  max: number
): PositionChipDisplay {
  if (filled === 0) {
    return 'empty';
  }
  if (filled < max) {
    return 'under';
  }
  if (filled === max) {
    return 'exact';
  }
  const depthThreshold = SKILL_POSITION_DEPTH_THRESHOLDS[label];
  if (depthThreshold !== undefined && filled - max >= depthThreshold) {
    return 'deep';
  }
  return 'plusOne';
}

/** Positions aggregated by player position (QB/RB/WR/TE) or roster slot (DEF/K/IDP). */
export interface AggregatedPositionSummary {
  label: string;
  filled: number;
  max: number;
  open: number;
  overLimit: boolean;
}

export interface TeamRosterCapSummaryRow {
  teamId: string;
  teamName: string;
  isUserTeam: boolean;
  capRemaining: number | null;
  capRemainingFormatted: string;
  committedSalary: number;
  openRosterSpots: number;
  maxRosterSpots: number;
  filledRosterSpots: number;
  positionSlots: AggregatedPositionSummary[];
}

const SKILL_POSITION_ORDER: PlayerPositionEnum[] = [
  PlayerPosition.Qb,
  PlayerPosition.Rb,
  PlayerPosition.Wr,
  PlayerPosition.Te,
];

const ROSTER_SLOT_POSITION_ORDER: RosterPositionEnum[] = [
  RosterPosition.Def,
  RosterPosition.K,
  RosterPosition.P,
  RosterPosition.Dl,
  RosterPosition.Lb,
  RosterPosition.Db,
];

const ROSTER_POSITIONS_EXCLUDED_FROM_SKILL_COUNT = new Set<RosterPositionEnum>([
  RosterPosition.Ir,
  RosterPosition.Ps,
]);

function getActiveRosterEntries(team: Team): Array<{ rosterPosition: RosterPositionEnum; playerInfo: TeamPlayerInfo }> {
  if (!team.playerRoster) {
    return [];
  }
  return Object.entries(team.playerRoster).flatMap(([rosterPosition, slots]) =>
    (slots ?? [])
      .filter((info) => info?.playerId && !info.contractExpired)
      .map((playerInfo) => ({
        rosterPosition: rosterPosition as RosterPositionEnum,
        playerInfo,
      }))
  );
}

export function countPlayersBySkillPosition(
  team: Team,
  playerMap: Map<string | undefined, Player | undefined>
): Map<PlayerPositionEnum, number> {
  const counts = new Map<PlayerPositionEnum, number>();

  getActiveRosterEntries(team).forEach(({ rosterPosition, playerInfo }) => {
    if (ROSTER_POSITIONS_EXCLUDED_FROM_SKILL_COUNT.has(rosterPosition)) {
      return;
    }
    const player = playerMap.get(playerInfo.playerId);
    const position = player?.position;
    if (!position || position === PlayerPosition.Invalid) {
      return;
    }
    counts.set(position, (counts.get(position) ?? 0) + 1);
  });

  return counts;
}

export function countPlayersByRosterSlot(
  team: Team,
  rosterPosition: RosterPositionEnum
): number {
  const slots = team.playerRoster?.[rosterPosition];
  if (!slots?.length) {
    return 0;
  }
  return slots.filter((info) => info?.playerId && !info.contractExpired).length;
}

export function getAggregatedPositionSummaries(
  league: League,
  team: Team,
  playerMap: Map<string | undefined, Player | undefined>
): AggregatedPositionSummary[] {
  const settings = league.rosterSettings;
  if (!settings) {
    return [];
  }

  const summaries: AggregatedPositionSummary[] = [];
  const skillCounts = countPlayersBySkillPosition(team, playerMap);

  SKILL_POSITION_ORDER.forEach((playerPosition) => {
    const max = settings[playerPosition] ?? 0;
    if (max <= 0) {
      return;
    }
    const filled = skillCounts.get(playerPosition) ?? 0;
    summaries.push({
      label: playerPosition,
      filled,
      max,
      open: Math.max(0, max - filled),
      overLimit: filled > max,
    });
  });

  ROSTER_SLOT_POSITION_ORDER.forEach((rosterPosition) => {
    const max = settings[rosterPosition] ?? 0;
    if (max <= 0) {
      return;
    }
    const filled = countPlayersByRosterSlot(team, rosterPosition);
    summaries.push({
      label: rosterPosition,
      filled,
      max,
      open: Math.max(0, max - filled),
      overLimit: filled > max,
    });
  });

  return summaries;
}

export function buildTeamRosterCapSummaryRow(
  league: League,
  team: Team,
  userId: string | undefined,
  playerMap: Map<string | undefined, Player | undefined>
): TeamRosterCapSummaryRow {
  const salaryCap = league.currentSeason?.nflSeason?.salaryCap;
  const committedSalary = team.currentSeasonSalary ?? 0;
  const capRemaining =
    salaryCap === undefined ? null : salaryCap - committedSalary;

  return {
    teamId: team.id!,
    teamName: team.name ?? 'Team',
    isUserTeam: team.owners?.some((owner) => owner.id === userId) ?? false,
    capRemaining,
    capRemainingFormatted: capRemaining === null ? 'N/A' : toSalary(capRemaining),
    committedSalary,
    openRosterSpots: getRemainingRosterSpots(team, league),
    maxRosterSpots: getMaxTeamSize(league),
    filledRosterSpots: getRosterSize(team),
    positionSlots: getAggregatedPositionSummaries(league, team, playerMap),
  };
}

export function buildLeagueRosterCapSummaryRows(
  league: League,
  userId: string | undefined,
  playerMap: Map<string | undefined, Player | undefined>
): TeamRosterCapSummaryRow[] {
  const teams = league.teams ?? [];
  return teams
    .map((team) => buildTeamRosterCapSummaryRow(league, team, userId, playerMap))
    .sort((a, b) => {
      if (a.isUserTeam !== b.isUserTeam) {
        return a.isUserTeam ? -1 : 1;
      }
      return a.teamName.localeCompare(b.teamName);
    });
}
